import {
  createMarkdownCard,
  createSectionedHorizontalCard,
} from '@/lib/idea-card-factory';
import type {
  IdeaCardBodyModel,
  IdeaCardBodySection,
  IdeaCardBodyMode,
  IdeaCardInputData,
  IdeaCardLayout,
  IdeaCardPreviewData,
  IdeaCardPreviewType,
  IdeaCardScoreUnit,
  ScoreRange,
} from '@/lib/idea-card-types';

export const STRUCTURED_CARDS_START = '<lema_cards>';
export const STRUCTURED_CARDS_END = '</lema_cards>';

// Minimum JSON length before attempting progressive parse (avoids parse spam on small chunks)
const MIN_PROGRESSIVE_PARSE_LENGTH = 20;
// Regex to check if JSON ends with closing structure (}, ], or }])
const JSON_CLOSING_REGEX = /[}\]]$/;

const getStreamingHoldbackLength = (text: string): number => {
  const maxPrefixLength = Math.min(text.length, STRUCTURED_CARDS_START.length - 1);
  for (let prefixLength = maxPrefixLength; prefixLength > 0; prefixLength -= 1) {
    if (text.endsWith(STRUCTURED_CARDS_START.slice(0, prefixLength))) {
      return prefixLength;
    }
  }
  return 0;
};

const PREVIEW_TYPES: IdeaCardPreviewType[] = [
  'instagram_profile',
  'tiktok_profile',
  'youtube_channel',
  'instagram_reel',
  'instagram_post',
  'instagram_carousel',
  'instagram_story',
  'tiktok_video',
  'youtube_short',
  'youtube_video',
  'calendar',
  'comparison_view',
  'chart',
];

const CARD_LAYOUTS: IdeaCardLayout[] = ['title', 'horizontal', 'vertical'];
const CARD_BODY_MODELS: IdeaCardBodyModel[] = ['markdown', 'sectioned_horizontal'];
const CARD_BODY_MODES: IdeaCardBodyMode[] = ['below', 'hidden'];
const SCORE_UNITS: IdeaCardScoreUnit[] = ['percent', 'count', 'rating', 'rate', 'currency'];

type StructuredCardsPayload = {
  cards: IdeaCardInputData[];
};

type ExtractStructuredCardsOptions = {
  final?: boolean;
};

export type CardBlock = {
  cards: IdeaCardInputData[];
  position: number; // character position in original text where this block appeared
};

export type StructuredAssistantResponse = {
  content: string;
  cards: IdeaCardInputData[]; // All cards flattened for backward compatibility
  cardBlocks: CardBlock[]; // Cards grouped by their original block position
  hasStructuredCards: boolean;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toStringMap = (value: unknown): Record<string, string> => {
  if (!isObject(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entryValue]) => entryValue !== undefined && entryValue !== null)
      .map(([key, entryValue]) => {
        if (typeof entryValue === 'string') {
          // If it's already a string that looks like JSON, try to parse and re-stringify to ensure proper escaping
          const trimmed = entryValue.trim();
          if (trimmed.length >= 2 &&
              ((trimmed.startsWith('[') && trimmed.endsWith(']')) ||
               (trimmed.startsWith('{') && trimmed.endsWith('}')))) {
            try {
              const parsed = JSON.parse(trimmed);
              return [key, JSON.stringify(parsed)];
            } catch {
              // If parsing fails, keep original string
              return [key, entryValue];
            }
          }
          return [key, entryValue];
        }

        if (typeof entryValue === 'number' || typeof entryValue === 'boolean') {
          return [key, String(entryValue)];
        }

        if (Array.isArray(entryValue) || isObject(entryValue)) {
          try {
            return [key, JSON.stringify(entryValue)];
          } catch {
            return [key, String(entryValue)];
          }
        }

        return [key, String(entryValue)];
      })
  );
};

const normalizePreview = (rawCard: Record<string, unknown>): IdeaCardPreviewData | undefined => {
  const rawPreview = isObject(rawCard.preview)
    ? rawCard.preview
    : rawCard.type
      ? {
          type: rawCard.type,
          props: rawCard.metadata,
        }
      : null;

  if (!isObject(rawPreview)) {
    return undefined;
  }

  const type = typeof rawPreview.type === 'string' ? rawPreview.type.trim() as IdeaCardPreviewType : undefined;
  if (!type || !PREVIEW_TYPES.includes(type)) {
    return undefined;
  }

  const label = typeof rawPreview.label === 'string'
    ? rawPreview.label.trim()
    : typeof rawPreview.previewLabel === 'string'
      ? rawPreview.previewLabel.trim()
      : undefined;

  return {
    type,
    label: label || undefined,
    props: toStringMap(rawPreview.props),
  };
};

const normalizeBodyMode = (rawCard: Record<string, unknown>, preview?: IdeaCardPreviewData): IdeaCardBodyMode | undefined => {
  const rawBodyMode = typeof rawCard.bodyMode === 'string'
    ? rawCard.bodyMode.trim().toLowerCase()
    : typeof rawCard.body_mode === 'string'
      ? rawCard.body_mode.trim().toLowerCase()
      : undefined;

  if (rawBodyMode && CARD_BODY_MODES.includes(rawBodyMode as IdeaCardBodyMode)) {
    return rawBodyMode as IdeaCardBodyMode;
  }

  return preview ? 'hidden' : undefined;
};

const normalizeBodyModel = (rawCard: Record<string, unknown>): IdeaCardBodyModel | undefined => {
  const rawBodyModel = typeof rawCard.bodyModel === 'string'
    ? rawCard.bodyModel.trim().toLowerCase()
    : typeof rawCard.body_model === 'string'
      ? rawCard.body_model.trim().toLowerCase()
      : undefined;

  if (rawBodyModel && CARD_BODY_MODELS.includes(rawBodyModel as IdeaCardBodyModel)) {
    return rawBodyModel as IdeaCardBodyModel;
  }

  return undefined;
};

const normalizeBodySections = (rawCard: Record<string, unknown>): IdeaCardBodySection[] | undefined => {
  const rawSections = Array.isArray(rawCard.bodySections)
    ? rawCard.bodySections
    : Array.isArray(rawCard.body_sections)
      ? rawCard.body_sections
      : undefined;

  if (!rawSections) {
    return undefined;
  }

  const sections = rawSections
    .map((section) => {
      if (!isObject(section)) {
        return null;
      }

      const label = typeof section.label === 'string' ? section.label.trim() : undefined;
      const content = typeof section.content === 'string' ? section.content.trim() : '';
      if (!content) {
        return null;
      }

      return { label, content };
    })
    .filter((section): section is IdeaCardBodySection => section !== null);

  return sections.length > 0 ? sections : undefined;
};

const normalizeCard = (rawCard: unknown): IdeaCardInputData | null => {
  if (!isObject(rawCard)) {
    return null;
  }

  const title = typeof rawCard.title === 'string' ? rawCard.title.trim() : '';
  if (!title) {
    return null;
  }

  const preview = normalizePreview(rawCard);
  const layout = typeof rawCard.layout === 'string' && CARD_LAYOUTS.includes(rawCard.layout as IdeaCardLayout)
    ? rawCard.layout as IdeaCardLayout
    : undefined;
  const bodyModel = normalizeBodyModel(rawCard);
  const bodySections = normalizeBodySections(rawCard);
  const bodyMode = normalizeBodyMode(rawCard, preview);
  const body = typeof rawCard.body === 'string' && rawCard.body.trim().length > 0
    ? rawCard.body.trim()
    : undefined;

  // Handle score (single value)
  const rawScore = rawCard.score;
  const score = typeof rawScore === 'number'
    ? rawScore
    : typeof rawScore === 'string' && rawScore.trim().length > 0
      ? Number(rawScore)
      : undefined;

  // Handle scoreRange
  const rawScoreRange = rawCard.scoreRange ?? rawCard.score_range;
  let scoreRange: ScoreRange | undefined = undefined;
  if (isObject(rawScoreRange) && 'lower' in rawScoreRange && 'upper' in rawScoreRange) {
    const lower = typeof rawScoreRange.lower === 'number' ? rawScoreRange.lower : undefined;
    const upper = typeof rawScoreRange.upper === 'number' ? rawScoreRange.upper : undefined;
    if (lower !== undefined && upper !== undefined && Number.isFinite(lower) && Number.isFinite(upper)) {
      scoreRange = {
        lower,
        upper,
        unit: typeof rawScoreRange.unit === 'string' ? rawScoreRange.unit : undefined
      };
    }
  }

  const getRawString = (k1: string, k2: string) => {
    const val = rawCard[k1] ?? rawCard[k2];
    return (typeof val === 'string' ? val.trim() : undefined) || undefined;
  };

  const rawScoreUnit = getRawString('scoreUnit', 'score_unit')?.toLowerCase();
  const scoreUnit = rawScoreUnit && SCORE_UNITS.includes(rawScoreUnit as IdeaCardScoreUnit)
    ? rawScoreUnit as IdeaCardScoreUnit
    : undefined;

  const scoreLabel = getRawString('scoreLabel', 'score_label');
  const groupLabel = getRawString('groupLabel', 'group_label');
  const infoTooltip = getRawString('infoTooltip', 'info_tooltip');

  if (bodyModel === 'sectioned_horizontal' || bodySections) {
    if (!bodySections || bodySections.length === 0) {
      return null;
    }

    return createSectionedHorizontalCard({
      title,
      bodySections,
      score: typeof score === 'number' && !Number.isNaN(score) ? score : undefined,
      scoreRange,
      scoreUnit,
      scoreLabel,
      bodyMode,
      preview,
      groupLabel,
      infoTooltip,
    });
  }

  return createMarkdownCard({
    title,
    body,
    score: typeof score === 'number' && !Number.isNaN(score) ? score : undefined,
    scoreRange,
    scoreUnit,
    scoreLabel,
    layout,
    bodyMode,
    preview,
    groupLabel,
    infoTooltip,
  });
};

export const parseStructuredCardsPayload = (jsonText: string): StructuredCardsPayload | null => {
  try {
    const parsed = JSON.parse(jsonText) as unknown;
    if (!isObject(parsed) || !Array.isArray(parsed.cards)) {
      return null;
    }

    const cards = parsed.cards
      .map(normalizeCard)
      .filter((card): card is IdeaCardInputData => card !== null);

    if (cards.length === 0) {
      return null;
    }

    return { cards };
  } catch {
    return null;
  }
};

/**
 * Extract all <lema_cards> blocks from text, preserving their inline positions.
 * Returns structured response with both flattened cards (backward compat) and positioned card blocks.
 */
export const extractStructuredAssistantResponse = (
  rawText: string,
  options: ExtractStructuredCardsOptions = {}
): StructuredAssistantResponse => {
  const final = options.final ?? true;
  const firstStartIndex = rawText.indexOf(STRUCTURED_CARDS_START);

  if (firstStartIndex === -1) {
    if (final) {
      return { content: rawText, cards: [], cardBlocks: [], hasStructuredCards: false };
    }

    const holdback = getStreamingHoldbackLength(rawText);
    const safeLength = Math.max(rawText.length - holdback, 0);
    return {
      content: rawText.slice(0, safeLength),
      cards: [],
      cardBlocks: [],
      hasStructuredCards: false,
    };
  }

  // Extract all card blocks with their positions
  const cardBlocks: CardBlock[] = [];
  const allCards: IdeaCardInputData[] = [];
  let remainingText = rawText;
  let currentPosition = 0;
  let contentParts: string[] = [];

  while (true) {
    const startIndex = remainingText.indexOf(STRUCTURED_CARDS_START);
    if (startIndex === -1) {
      // No more card blocks - add remaining text with holdback if streaming
      if (!final) {
        // Apply holdback to prevent partial tags from leaking
        const holdback = getStreamingHoldbackLength(remainingText);
        const safeLength = Math.max(remainingText.length - holdback, 0);
        contentParts.push(remainingText.slice(0, safeLength));
      } else {
        contentParts.push(remainingText);
      }
      break;
    }

    // Add text before this card block
    const beforeBlock = remainingText.slice(0, startIndex);
    contentParts.push(beforeBlock);
    const blockPosition = currentPosition + beforeBlock.length;

    const afterStart = remainingText.slice(startIndex + STRUCTURED_CARDS_START.length);
    const endIndex = afterStart.indexOf(STRUCTURED_CARDS_END);

    if (endIndex === -1) {
      // Incomplete block - hide from display during streaming
      if (!final) {
        // Try progressive parse for streaming
        const potentialJson = afterStart.trim();
        if (potentialJson.length > MIN_PROGRESSIVE_PARSE_LENGTH && JSON_CLOSING_REGEX.test(potentialJson)) {
          const parsed = parseStructuredCardsPayload(potentialJson);
          if (parsed && parsed.cards.length > 0) {
            cardBlocks.push({
              cards: parsed.cards,
              position: blockPosition,
            });
            allCards.push(...parsed.cards);
          }
        }
      }
      // Don't add incomplete block to content - keeps tags hidden until complete
      break;
    }

    // Parse complete block
    const jsonText = afterStart.slice(0, endIndex).trim();
    const parsed = parseStructuredCardsPayload(jsonText);

    if (parsed && parsed.cards.length > 0) {
      cardBlocks.push({
        cards: parsed.cards,
        position: blockPosition,
      });
      allCards.push(...parsed.cards);
    }

    // Continue with text after this block
    remainingText = afterStart.slice(endIndex + STRUCTURED_CARDS_END.length);
    // Update position to where cards should be inserted (before the block tags)
    currentPosition = blockPosition;
  }

  // Don't trim - positions are calculated on untrimmed segments
  const content = contentParts.join('');

  return {
    content,
    cards: allCards, // Flattened for backward compatibility
    cardBlocks, // Positioned blocks for inline rendering
    hasStructuredCards: cardBlocks.length > 0,
  };
};
